import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import type { ApiUser } from '../store/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Send the Bearer token when we hold one; the httpOnly cookie is the fallback.
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// A 401 from these endpoints is not an expired session. Login 401 means bad
// credentials, /auth/me 401 means signed out, /auth/refresh 401 means the
// refresh token is gone. None of them should trigger a refresh attempt.
const SKIP_REFRESH_URL_PATTERNS = ['/auth/login', '/auth/refresh', '/auth/me'];

export function shouldSkipTokenRefresh(url?: string): boolean {
  if (!url) return false;
  return SKIP_REFRESH_URL_PATTERNS.some((pattern) => url.includes(pattern));
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const skipRefresh = shouldSkipTokenRefresh(originalRequest?.url);

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !skipRefresh) {
      originalRequest._retry = true;
      try {
        const { refreshToken } = useAuthStore.getState();
        const { data } = await api.post('/auth/refresh', { refresh_token: refreshToken });
        if (data.access_token) {
          useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
        }
        return api(originalRequest);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export interface LoginResponse {
  user: ApiUser;
  access_token: string;
  refresh_token: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }),

  me: () => api.get<{ user: ApiUser }>('/auth/me'),

  logout: () => api.post('/auth/logout'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ access_token: string; refresh_token: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  refresh: (refreshToken: string | null) =>
    api.post<{ access_token: string; refresh_token: string }>('/auth/refresh', {
      refresh_token: refreshToken,
    }),
};
